/**
 * What was said on one call, as a conversation somebody at a counter can read.
 *
 * NOT `Conversation`, WHICH THE LOG'S DIALOG KEEPS. That component interleaves the agent's
 * tool calls with the turns, at the position each happened, with their arguments a click
 * away - which is exactly right for an owner at a desk working out why the agent quoted the
 * wrong price, and noise for a mechanic who wants to know what the customer asked. This is
 * the turns and nothing else, as bubbles, at the register's type size.
 *
 * THE CALLER ON THE RIGHT, IN INK. The reader is the business, and in every messaging app
 * they have ever used the right-hand side is "me"; here "me" is the assistant answering for
 * them, but the thing being read for is the customer's words, so those are the ones given
 * the weight. `paper` on `ink` is pinned by the contrast gate.
 *
 * THE RECORDING RIDES THE FOOTER, so it stays on screen however far down the conversation
 * somebody has read - the same argument the dialog makes for putting it in its sticky head.
 */

import type { CallDetail } from "../api";
import { duration, fullWhen } from "../format";
import { cx } from "../ui";
import { Dialog } from "./Dialog";
import { RecordingPlayer } from "./RecordingPlayer";
import { titleOf } from "./SimpleRow";

export function SimpleTranscript({
  call,
  tz,
  onClose,
}: {
  call: CallDetail;
  tz: string | null | undefined;
  onClose: () => void;
}) {
  return (
    <Dialog
      size="list"
      title={<span className="text-xl">{titleOf(call)}</span>}
      meta={
        <span className="text-base">
          Called {fullWhen(call.startedAt, tz)} · {duration(call.durationMs)}
        </span>
      }
      onClose={onClose}
      footer={
        call.recording ? (
          <div className="grid w-full gap-1.5">
            <p className="text-base font-medium text-ink">Listen to the recording</p>
            <RecordingPlayer
              path={`/v1/calls/${call.id}/recording?v=${call.recording.sha256}`}
              durationMs={call.recording.durationMs}
              className="min-w-0"
            />
          </div>
        ) : undefined
      }
    >
      {call.transcript.length === 0 ? (
        <p className="py-6 text-center text-xl text-ink-soft">
          There is no transcript for this call.
        </p>
      ) : (
        <ol className="grid list-none gap-3 p-0">
          {call.transcript.map((turn, index) => {
            const caller = turn.role === "caller";
            return (
              <li
                key={index}
                className={cx("grid max-w-[85%] gap-1", caller ? "justify-self-end" : "justify-self-start")}
              >
                <span className={cx("text-sm text-ink-soft", caller && "text-right")}>
                  {caller ? "Caller" : "Your assistant"}
                </span>
                <p
                  className={cx(
                    "rounded-xl px-4 py-3 text-xl leading-snug",
                    caller ? "bg-ink text-paper" : "bg-sunken text-ink",
                  )}
                >
                  {turn.text}
                </p>
              </li>
            );
          })}
        </ol>
      )}
    </Dialog>
  );
}
